import {
  TabItem,
  Tabs,
  useModals,
  Modal,
  ModalProps,
  Box,
  Button,
  Input,
  LinkTo,
  Select,
  SelectItem,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { apiFiles } from "@/api/file.api.ts";
import { FileItem } from "@/components/files/FileItem.tsx";
import { FileUpload } from "@/components/files/FileUpload.tsx";
import { InputWithVariableInsert } from "@/components/input-with-variable-insert/InputWithVariableInsert.tsx";
import { getWorkflowPlaceholderOptions } from "@/components/rich-editor/rich-editor.types.ts";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { WorkflowActionTimingEditor } from "@/pages/workflows/edit/actions/WorkflowActionTimingEditor.tsx";
import {
  getWorkflowActionTimingPayload,
  getWorkflowActionTimingValues,
  useWorkflows,
} from "@/pages/workflows/workflow-hook.tsx";
import { useWorkflowBlockConditions } from "@/pages/workflows/edit/flow/use-workflow-block-conditions.tsx";
import { WorkflowBlockConditionsList } from "@/pages/workflows/edit/flow/WorkflowBlockConditionsList.tsx";
import { useEmailIntegrations } from "@/queries/integrations/use-emails.query.ts";
import { useTemplates } from "@/queries/use-templates.query.ts";
import { useWorkflowUpdateBlockActionCandidateEmail } from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiEntityTypes, ApiFile } from "@/types/common.types.ts";
import { ApiTemplate } from "@/types/template.types.ts";
import {
  ApiWorkflowBlockActionCandidateEmail,
  ApiWorkflowBlockActionCandidateEmailSave,
} from "@/types/workflows/workflow.action-notification.types.ts";
import { ApiWorkflowBlockActionTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowBlock, ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import * as yup from "yup";

export interface WorkflowEditActionCandidateEmailModalProps extends ModalProps {
  workflowVersion: ApiWorkflowVersion;
  block: ApiWorkflowBlock;
  actionCandidateEmail: ApiWorkflowBlockActionCandidateEmail;
}

type CandidateEmailFormValues = Omit<ApiWorkflowBlockActionCandidateEmailSave, "file_uuids">;

export const WorkflowEditActionCandidateEmailModal = ({
  workflowVersion,
  block,
  actionCandidateEmail,
  ...props
}: WorkflowEditActionCandidateEmailModalProps) => {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { workflowActionTimingSchemaFields } = useWorkflows();
  const schemaObject = yup.object({
    title: yup.string().defined().nullable(),
    ...workflowActionTimingSchemaFields,
    from_email: yup.string().required(t("errors:field_required")),
    subject: yup.string().required(t("errors:field_required")),
    body: yup.string().required(t("errors:field_required")),
    template_id: yup.number().defined().nullable(),
  });

  const { conditions, handleAddCondition, handleEditCondition, handleDeleteCondition } =
    useWorkflowBlockConditions(workflowVersion, block);

  const form = useForm<CandidateEmailFormValues>({
    mode: "onSubmit",
    resolver: yupResolver(schemaObject),
    values: {
      ...getWorkflowActionTimingValues(block),
      title: block.title ?? "",
      from_email: actionCandidateEmail.from_email ?? "",
      subject: actionCandidateEmail.subject,
      body: actionCandidateEmail.body,
      template_id: actionCandidateEmail.template_id,
    },
  });
  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const eventType = workflowVersion.trigger_event?.event_type;

  const { data: emailIntegrations, isLoading: isEmailIntegrationsLoading } = useEmailIntegrations();
  const { data: emailTemplates } = useTemplates();

  const updateBlockActionCandidateEmail = useWorkflowUpdateBlockActionCandidateEmail(control);

  const [existingUuids, setExistingUuids] = useState<string[]>(actionCandidateEmail.file_uuids);
  const [files, setFiles] = useState<ApiFile[]>([]);

  const fromEmail = watch("from_email");
  const inactiveFromEmails =
    emailIntegrations
      ?.filter((integration) => !integration.is_active)
      .map(({ identity }) => identity) ?? [];
  const selectedIntegrationIsInactive = inactiveFromEmails.includes(fromEmail);

  const emailIntegrationOptions: SelectItem[] =
    emailIntegrations?.map((integration) => ({
      id: integration.identity,
      title: integration.identity,
    })) ?? [];
  const fromEmailOptions =
    fromEmail && !emailIntegrations?.some((integration) => integration.identity === fromEmail)
      ? [...emailIntegrationOptions, { id: fromEmail, title: fromEmail }]
      : emailIntegrationOptions;

  const handleApplyTemplate = (template: ApiTemplate) => {
    setValue("subject", template.subject ?? "", { shouldValidate: true });
    setValue("body", template.body, { shouldValidate: true });
    setValue("template_id", template.id, { shouldValidate: true });
  };

  const removeExistingFile = async (uuid: string) => {
    setExistingUuids((prev) => prev.filter((value) => value !== uuid));
    await apiFiles.delete(uuid);
  };

  const removeFile = async (file: ApiFile) => {
    setFiles((prev) => prev.filter((f) => f.id !== file.id));
    await apiFiles.delete(file.uuid);
  };

  const handleSave = handleSubmit(async (data) => {
    try {
      await updateBlockActionCandidateEmail.mutateAsync({
        workflowId: workflowVersion.workflow_id,
        workflowVersionId: workflowVersion.id,
        workflowBlockId: block.id,
        payload: {
          title: data.title,
          ...getWorkflowActionTimingPayload(data),
          block_data: {
            ...data,
            file_uuids: [...existingUuids, ...files.map((file) => file.uuid)],
          },
          conditions: conditions.map((x) => ({ ...x })),
        },
      });

      analyticsService.trackEvent(analyticEvents.workflows.blockEdited, {
        [analyticProperties.id]: workflowVersion.workflow_id,
        [analyticProperties.actionType]: ApiWorkflowBlockActionTypes.candidate_email,
      });

      close(id);
    } catch {
      // handle error
    }
  });

  const [activeTab, setActiveTab] = useState(0);

  return (
    <>
      <Modal
        {...props}
        variant={"side"}
        title={t("workflow:action_type_candidate_email")}
        layout={"stretch"}
        withCloser
        closeByEscEnabled
        size={"lg"}
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              size={"lg"}
              className={"w-full"}
              type={"submit"}
              onClick={handleSave}
              isLoading={updateBlockActionCandidateEmail.isPending}
            >
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={handleSave}>
          <Tabs onActiveTabChange={setActiveTab} activeTab={activeTab}>
            <TabItem title={t("common:message")}>
              <Stack gap={"lg"} className={"py-6"}>
                <Stack>
                  <Controller
                    render={({ field: { value, onChange } }) => (
                      <Select
                        label={t("core:email_from")}
                        emptyTitle={t("common:select")}
                        isLoading={isEmailIntegrationsLoading}
                        options={fromEmailOptions}
                        value={value}
                        onChange={onChange}
                        required={true}
                        renderSelectedTitle={() => (
                          <Text
                            className={selectedIntegrationIsInactive ? "text-danger" : undefined}
                          >
                            {value}
                          </Text>
                        )}
                        error={
                          selectedIntegrationIsInactive ? undefined : errors.from_email?.message
                        }
                      />
                    )}
                    control={control}
                    name={"from_email"}
                  />
                  {selectedIntegrationIsInactive && (
                    <Text className={"text-danger text-sm py-2"}>
                      <Trans
                        i18nKey="ats:email_integration_expired_error_with_link"
                        components={{
                          reconnect: (
                            <a
                              href="/account-settings/integrations"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline hover:no-underline"
                            />
                          ),
                        }}
                      />
                    </Text>
                  )}
                  {!isEmailIntegrationsLoading && emailIntegrations?.length === 0 && (
                    <Text className={"text-secondary text-sm py-2"}>
                      <LinkTo
                        to={"/account-settings/integrations"}
                        target={"_blank"}
                        rel={"noopener noreferrer"}
                      >
                        {t("integrations:connect")}
                      </LinkTo>
                    </Text>
                  )}
                </Stack>

                <Stack gap={"md"}>
                  <Title
                    size="xs"
                    className="-mb-(--gap-sm)"
                    header={t("workflow:action_type_notification_message_text")}
                  />
                  <InputWithVariableInsert
                    label={t("core:email_subject")}
                    type="text"
                    {...register("subject")}
                    required={true}
                    error={errors.subject?.message}
                    variableOptions={getWorkflowPlaceholderOptions(eventType)}
                  />
                  <Stack>
                    <Title
                      header={t("workflow:action_type_notification_body")}
                      size={"xs"}
                      paddingBottom={true}
                      required={true}
                    />
                    <Controller
                      control={control}
                      name={"body"}
                      render={({ field: { value, onChange } }) => (
                        <RichEditor
                          value={value ?? ""}
                          onChange={onChange}
                          minHeight={100}
                          error={errors.body?.message}
                          placeholderOptions={getWorkflowPlaceholderOptions(eventType)}
                          emailTemplates={emailTemplates}
                          onApplyTemplate={handleApplyTemplate}
                        />
                      )}
                    />
                  </Stack>

                  <Stack gap={"sm"} items={"start"}>
                    {(existingUuids.length > 0 || files.length > 0) && (
                      <Stack gap={"sm"}>
                        {existingUuids.map((uuid) => (
                          <FileItem
                            key={uuid}
                            file={{ uuid, name: t("common:file") } as ApiFile}
                            showSize={false}
                            onRemove={() => void removeExistingFile(uuid)}
                          />
                        ))}
                        {files.map((file) => (
                          <FileItem
                            key={`file${file.id}`}
                            file={file}
                            onRemove={() => void removeFile(file)}
                          />
                        ))}
                      </Stack>
                    )}
                    <Box className="-mt-(--gap-sm)">
                      <FileUpload
                        entityId={block.id}
                        entityType={ApiEntityTypes.other}
                        afterSend={(file) => {
                          if (file) setFiles((prev) => [...prev, file]);
                        }}
                        buttonText={t("common:attach")}
                      />
                    </Box>
                  </Stack>
                </Stack>
              </Stack>
            </TabItem>
            <TabItem title={t("workflow:conditions")}>
              <Stack gap={"lg"} className={"py-6"}>
                <Stack>
                  <Title header={t("workflow:block")} size={"xs"} />
                  <Input
                    label={t("common:title")}
                    type="text"
                    {...register("title")}
                    error={errors.title?.message}
                  />
                </Stack>
                <WorkflowActionTimingEditor
                  control={control}
                  register={register}
                  errors={errors}
                  triggerDateAvailable={workflowVersion.trigger_schedule_rule !== null}
                />
                <WorkflowBlockConditionsList
                  conditions={conditions}
                  onAdd={handleAddCondition}
                  onEdit={handleEditCondition}
                  onDelete={handleDeleteCondition}
                />
              </Stack>
            </TabItem>
          </Tabs>
        </form>
      </Modal>
    </>
  );
};
